// src/components/fix-actions/fix-actions-loading.tsx
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";

const STAGES = [
  {
    id: "prioritize",
    headline: "Ranking opportunities",
    body: "Evaluating gaps across AI models to find the highest-impact improvements.",
    bgGradient:
      "radial-gradient(1200px 900px at 20% 100%, var(--alpha-teal-10) 0%, var(--background) 70%)",
    terminalLogs: [
      "Analyzing competitive gaps...",
      "→ Ranking by impact score",
      "Calculating effort",
    ],
  },
  {
    id: "strategy",
    headline: "Capturing fix actions",
    body: "Drafting technical maneuvers and content updates to realign your AI narrative.",
    bgGradient:
      "radial-gradient(1200px 900px at 80% 100%, var(--signal-blue-10) 0%, var(--background) 70%)",
    terminalLogs: [
      "Synthesizing maneuver logic...",
      "Defining target attributes",
      "Aligning brand tone",
    ],
  },
  {
    id: "ready",
    headline: "Your actions are ready",
    body: "The optimization strategy is complete and ready for implementation.",
    bgGradient:
      "radial-gradient(1200px 900px at 50% 50%, var(--alpha-teal-10) 0%, var(--background) 80%)",
    terminalLogs: [
      "✓ Report generated",
      "✓ 14 actions identified",
      "Priming Studio...",
    ],
  },
];

export function FixActionsLoading({
  brandName,
  isDone,
}: {
  brandName: string;
  isDone: boolean;
}) {
  const [internalProgress, setInternalProgress] = useState(0);

  const stageIndex = isDone ? 2 : internalProgress > 50 ? 1 : 0;
  const stage = STAGES[stageIndex];

  useEffect(() => {
    if (isDone) {
      setInternalProgress(100);
      return;
    }

    const timer = setInterval(() => {
      setInternalProgress((prev) => {
        if (prev >= 92) return 92;
        const increment = Math.random() * 2;
        return Math.min(92, prev + increment);
      });
    }, 150);

    return () => clearInterval(timer);
  }, [isDone]);

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden transition-all duration-1000"
      style={{ background: stage.bgGradient }}
    >
      {/* Centered Brand Header */}
      <div className="absolute top-6 sm:top-12 left-0 right-0 z-20 flex justify-center px-4">
        <div
          className="text-[10px] font-black uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[var(--foreground)] opacity-70 border border-[var(--border)] px-4 sm:px-5 py-2 rounded-full backdrop-blur-md bg-[var(--background)]/30 truncate max-w-full"
        >
          {brandName}
        </div>
      </div>

      {/* Hero — scales from ~text-4xl on mobile up to 7xl on desktop */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 md:px-16 text-center z-10">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <h1
            className="font-bold tracking-tighter mb-4 sm:mb-6 transition-all"
            style={{
              fontSize: "clamp(2rem, 8vw, 4.5rem)",
              lineHeight: 1.05,
            }}
          >
            {stage.headline}
          </h1>
          <p
            className="text-[var(--muted-foreground)] max-w-xl mx-auto"
            style={{ fontSize: "clamp(var(--text-base), 2.5vw, var(--text-xl))" }}
          >
            {stage.body}
          </p>
        </div>
      </div>

      <div className="p-6 sm:p-10 md:p-12 z-20 bg-[var(--card)]/40 backdrop-blur-xl border-t border-[var(--border)]">
        <div className="max-w-md mx-auto space-y-4">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest gap-2">
            <span className="flex items-center gap-2 min-w-0">
              {isDone ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-[var(--alpha-teal)] shrink-0" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--signal-blue)] shrink-0" />
              )}
              <span className="truncate">
                {isDone ? "Strategy Complete" : "Processing Optimization Plan"}
              </span>
            </span>
            <span
              className="shrink-0"
              style={{ color: isDone ? "var(--alpha-teal)" : "inherit" }}
            >
              {Math.floor(internalProgress)}%
            </span>
          </div>
          <div className="h-1.5 w-full bg-[var(--muted)] rounded-full overflow-hidden border border-[var(--border)]">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{
                width: `${internalProgress}%`,
                backgroundColor: isDone ? "var(--alpha-teal)" : "var(--foreground)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}